import * as THREE from 'three';
import {
  BOT_BUILDS,
  BOT_RANK_PRIORITY,
  HERO_ORDER,
  MAX_LEVEL,
  RANKS,
  TICK_RATE,
  abilityDuration,
  attackDamageRatio,
  brawlRoster,
  getHeroDef,
  getItem,
  heroStats,
  type AbilitySlot,
  type HeroId,
  type HeroState,
  type HeroStats,
} from '@moba/shared';
import { KIT_DESCRIPTIONS, KIT_NAMES, abilityIcon, type KitSlot } from './abilityIcons.ts';
import { abilityTooltipData } from './abilityTooltip.ts';
import { instantiateCharacter, type CharacterAsset, type CharacterManifestEntry } from './assets.ts';

import { t } from './i18n.ts';
/** Rol de cada héroe (selección y galería). */
export const HERO_ROLES: Record<HeroId, string> = {
  knight: t('Espadachín'),
  barbarian: t('Tanque'),
  mage: t('Tiradora'),
  falco: t('Tirador'),
  mario: t('Luchador'),
  sheik: t('Asesina'),
};

/** Rol de compra de los bots (mismo criterio que `botRole` en la simulación). */
const BUILD_ROLE: Record<HeroId, keyof typeof BOT_BUILDS> = {
  knight: 'fighter',
  barbarian: 'tank',
  mage: 'marksman',
  falco: 'marksman',
  mario: 'fighter',
  sheik: 'assassin',
};

const CLIP_LABELS: Record<string, string> = {
  idle: 'Reposo',
  run: 'Correr',
  attack1: 'Ataque 1',
  attack2: 'Ataque 2',
  attack3: 'Ataque 3',
  cast_q: 'Habilidad Q',
  cast_w: 'Habilidad W',
  cast_e: 'Habilidad E',
  cast_r: 'Habilidad R',
  hit: 'Recibir golpe',
  death: 'Muerte',
};

const SLOTS: AbilitySlot[] = ['q', 'w', 'e', 'r'];

function num(value: number, digits = 1): string {
  const rounded = Math.round(value * 10 ** digits) / 10 ** digits;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(digits);
}

function secs(ticks: number): string {
  return `${num(ticks / TICK_RATE, 2)} s`;
}

function pct(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

function h<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}

/** Clips con efectos visuales propios declarados en el manifiesto, en lenguaje llano. */
function visualEffects(manifest: CharacterManifestEntry): string[] {
  const label = (key: string): string => t(CLIP_LABELS[key] ?? key);
  const list = (keys: string[]): string => keys.map(label).join(', ');
  const out: string[] = [];
  if (manifest.loop?.length) out.push(t('En bucle durante toda la acción: {clips}.', { clips: list(manifest.loop) }));
  if (manifest.bow) out.push(t('Arco generado en la mano durante {clips}.', { clips: label(manifest.bow.clip) }));
  if (manifest.bowPose) out.push(t('Pose procedural de arquero (brazo adelantado, mano en la mejilla).'));
  if (manifest.blaster) out.push(t('Blaster de Melee en la mano durante {clips}.', { clips: list(manifest.blaster.clips) }));
  for (const image of manifest.afterimages ?? []) out.push(t('Copias fantasma al moverse durante {clips}.', { clips: list(image.clips) }));
  if (manifest.burn) out.push(t('El cuerpo brilla en llamas durante {clips}.', { clips: list(manifest.burn.clips) }));
  if (manifest.fireball) out.push(t('Se convierte en bola de fuego con estela durante {clips}.', { clips: list(manifest.fireball.clips) }));
  if (manifest.jump) {
    out.push(t(manifest.jump.coins ? 'Salto de {height} u con polvo y monedas durante {clips}.' : 'Salto de {height} u con polvo durante {clips}.', { height: num(manifest.jump.height), clips: list(manifest.jump.clips) }));
  }
  if (manifest.breath) {
    out.push(t('Aliento de fuego desde la boca ({range} u, {arc}°) durante {clips}.', { range: num(manifest.breath.range), arc: manifest.breath.arc, clips: list(manifest.breath.clips) }));
  }
  if (manifest.shield) out.push(t('Reflector hexagonal de energía durante {clips}.', { clips: list(manifest.shield.clips) }));
  if (manifest.reflectorPose) out.push(t('Pose procedural del Reflector: de frente a la cámara, en cuclillas, piernas abiertas.'));
  return out;
}

/** Visor 3D aparte (renderer pequeño) con el modelo del héroe y sus clips. */
class ModelViewer {
  readonly canvas = h('canvas', 'gallery-canvas');
  private renderer?: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(32, 1, 0.05, 100);
  private lastFrame = 0;
  private mixer?: THREE.AnimationMixer;
  private current?: THREE.AnimationAction;
  private instance?: { root: THREE.Group; model: THREE.Object3D; clips: THREE.AnimationClip[] };
  /** pies del modelo (manifiesto `feet`) y su altura en la pose de enlace: el idle no debe flotar */
  private footBones: THREE.Object3D[] = [];
  private footBaseline = 0;
  private modelBaseY = 0;
  private groundOffset = 0;
  private currentClip = '';
  private readonly footTmp = new THREE.Vector3();
  private asset?: CharacterAsset;
  private frameId = 0;
  private yaw = 0.5;
  private dragging = false;
  private lastX = 0;
  autoRotate = true;
  loop = false;
  speed = 1;
  /** clave del clip en curso (del manifiesto) o nombre crudo del GLB */
  onClipChange?: (clipName: string) => void;

  constructor() {
    this.scene.add(new THREE.HemisphereLight(0xdfeeff, 0x1a2230, 1.7));
    const key = new THREE.DirectionalLight(0xffffff, 2.3);
    key.position.set(3, 5, 4);
    this.scene.add(key);
    const rim = new THREE.DirectionalLight(0x8fd0ff, 1.2);
    rim.position.set(-3, 3, -4);
    this.scene.add(rim);
    const floor = new THREE.Mesh(
      new THREE.CircleGeometry(1.15, 48),
      new THREE.MeshBasicMaterial({ color: 0x5fb2ff, transparent: true, opacity: 0.12 }),
    );
    floor.rotation.x = -Math.PI / 2;
    this.scene.add(floor);
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(1.09, 1.15, 64),
      new THREE.MeshBasicMaterial({ color: 0x8fd0ff, transparent: true, opacity: 0.5 }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.002;
    this.scene.add(ring);

    this.canvas.addEventListener('pointerdown', (event) => {
      this.dragging = true;
      this.lastX = event.clientX;
      this.canvas.setPointerCapture(event.pointerId);
    });
    this.canvas.addEventListener('pointermove', (event) => {
      if (!this.dragging) return;
      this.yaw += (event.clientX - this.lastX) * 0.012;
      this.lastX = event.clientX;
    });
    const stop = (): void => { this.dragging = false; };
    this.canvas.addEventListener('pointerup', stop);
    this.canvas.addEventListener('pointercancel', stop);
  }

  setHero(asset: CharacterAsset): void {
    this.instance?.root.removeFromParent();
    this.mixer?.stopAllAction();
    this.asset = asset;
    const instance = instantiateCharacter(asset);
    this.instance = instance;
    this.scene.add(instance.root);
    this.findFeet(asset.manifest.feet ?? []);
    this.mixer = new THREE.AnimationMixer(instance.model);
    this.mixer.addEventListener('finished', () => {
      if (!this.loop) this.play(asset.manifest.clips.idle, true);
    });
    this.current = undefined;
    this.play(asset.manifest.clips.idle, true);
  }

  /** Reproduce un clip del GLB por su nombre. */
  play(clipName: string, forceLoop = false): void {
    if (!this.mixer || !this.instance) return;
    const clip = THREE.AnimationClip.findByName(this.instance.clips, clipName);
    if (!clip) return;
    const action = this.mixer.clipAction(clip);
    const looping = forceLoop || this.loop;
    action.reset();
    action.setLoop(looping ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);
    action.clampWhenFinished = !looping;
    if (this.current && this.current !== action) action.crossFadeFrom(this.current, 0.15, false);
    action.play();
    this.current = action;
    this.currentClip = clipName;
    this.onClipChange?.(clipName);
  }

  start(): void {
    if (this.frameId) return;
    if (!this.renderer) {
      this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true });
      this.renderer.outputColorSpace = THREE.SRGBColorSpace;
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    }
    this.lastFrame = performance.now();
    const frame = (): void => {
      this.frameId = requestAnimationFrame(frame);
      this.render();
    };
    this.frameId = requestAnimationFrame(frame);
  }

  stop(): void {
    cancelAnimationFrame(this.frameId);
    this.frameId = 0;
  }

  private findFeet(names: readonly string[]): void {
    const instance = this.instance;
    this.footBones = [];
    this.groundOffset = 0;
    if (!instance) return;
    this.modelBaseY = instance.model.position.y;
    for (const name of names) {
      const bone = instance.model.getObjectByName(name);
      if (bone) this.footBones.push(bone);
    }
    if (this.footBones.length > 0) this.footBaseline = this.lowestFootY();
  }

  private lowestFootY(): number {
    const root = this.instance!.root;
    root.updateMatrixWorld(true);
    let lowest = Infinity;
    for (const bone of this.footBones) {
      const y = root.worldToLocal(bone.getWorldPosition(this.footTmp)).y;
      if (y < lowest) lowest = y;
    }
    return lowest;
  }

  /**
   * Igual que en partida (`HeroView.applyIdleGrounding`): los idles de KayKit
   * retargeteados suben la cadera sin doblar las piernas; en el idle se baja el
   * modelo lo que subió el pie más bajo (nunca lo hunde bajo el suelo).
   */
  private applyIdleGrounding(dt: number): void {
    const instance = this.instance;
    if (!instance || this.footBones.length === 0) return;
    const idle = this.currentClip === this.asset?.manifest.clips.idle;
    if (idle) {
      instance.model.position.y = this.modelBaseY;
      this.groundOffset = Math.max(0, this.lowestFootY() - this.footBaseline);
    } else if (this.groundOffset > 0) {
      this.groundOffset = Math.max(0, this.groundOffset - dt * 1.5);
    } else {
      return;
    }
    instance.model.position.y = this.modelBaseY - this.groundOffset;
  }

  private render(): void {
    const renderer = this.renderer;
    if (!renderer) return;
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    if (width === 0 || height === 0) return;
    const size = renderer.getSize(new THREE.Vector2());
    if (size.x !== width || size.y !== height) {
      renderer.setSize(width, height, false);
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
    }
    const now = performance.now();
    const dt = Math.min((now - this.lastFrame) / 1000, 0.1);
    this.lastFrame = now;
    this.mixer?.update(dt * this.speed);
    this.applyIdleGrounding(dt);
    if (this.autoRotate && !this.dragging) this.yaw += dt * 0.35;
    if (this.instance) this.instance.root.rotation.y = this.yaw;
    const tall = this.asset?.manifest.targetHeight ?? 2;
    const dist = tall * 2.1 + 1.2;
    this.camera.position.set(0, tall * 0.62, dist);
    this.camera.lookAt(0, tall * 0.48, 0);
    renderer.render(this.scene, this.camera);
  }
}

/**
 * Galería de personajes (fuera de partida): visor 3D con todas las
 * animaciones, estadísticas por nivel, kit completo con valores por rango y
 * todo lo demás que el juego define para cada héroe.
 */
export class Gallery {
  private readonly viewer = new ModelViewer();
  private heroId: HeroId = HERO_ORDER[0];
  private level = 1;
  private withBuild = false;
  private ranks: Record<AbilitySlot, number> = { q: 1, w: 1, e: 1, r: 1 };
  private readonly tabs = new Map<HeroId, HTMLButtonElement>();
  private readonly clipButtons = new Map<string, HTMLButtonElement>();
  private readonly nameEl = h('div', 'gallery-name');
  private readonly tagsEl = h('div', 'gallery-tags');
  private readonly clipsEl = h('div', 'gallery-clips');
  private readonly rawClipsEl = h('div', 'gallery-clips raw');
  private readonly rawClipsSummary = h('summary');
  private readonly clipInfoEl = h('div', 'gallery-clip-info');
  private readonly fxEl = h('ul', 'gallery-fx');
  private readonly statsEl = h('div', 'gallery-stats');
  private readonly kitEl = h('div', 'gallery-kit');
  private readonly extraEl = h('div', 'gallery-extra');
  private readonly levelInput = h('input');
  private readonly levelValue = h('b');

  constructor(
    private readonly root: HTMLElement,
    private readonly assets: Map<HeroId, CharacterAsset>,
    private readonly portraits: Map<HeroId, string>,
  ) {
    this.build();
    this.viewer.onClipChange = (clipName) => this.markClip(clipName);
    this.select(this.heroId);
  }

  show(heroId?: HeroId): void {
    if (heroId) this.select(heroId);
    this.viewer.start();
  }

  hide(): void {
    this.viewer.stop();
  }

  private build(): void {
    this.root.replaceChildren();
    const tabs = h('nav', 'gallery-tabs');
    for (const id of HERO_ORDER) {
      const tab = h('button', 'gallery-tab');
      tab.type = 'button';
      const portrait = this.portraits.get(id);
      if (portrait) {
        const img = h('img');
        img.src = portrait;
        img.alt = '';
        tab.appendChild(img);
      }
      tab.append(h('span', undefined, getHeroDef(id).displayName));
      tab.addEventListener('click', () => this.select(id));
      tabs.appendChild(tab);
      this.tabs.set(id, tab);
    }

    // columna izquierda: visor + animaciones
    const left = h('div', 'gallery-left');
    const stage = h('div', 'gallery-stage');
    stage.append(this.viewer.canvas, this.nameEl, this.tagsEl);
    const hint = h('div', 'gallery-hint', t('Arrastra para girar'));
    stage.appendChild(hint);

    const controls = h('div', 'gallery-controls');
    const loop = this.toggle(t('En bucle'), false, (on) => { this.viewer.loop = on; });
    const rotate = this.toggle(t('Girar solo'), true, (on) => { this.viewer.autoRotate = on; });
    const speed = h('label', 'gallery-speed');
    const speedInput = h('input');
    speedInput.type = 'range';
    speedInput.min = '0.1';
    speedInput.max = '2';
    speedInput.step = '0.1';
    speedInput.value = '1';
    const speedValue = h('b', undefined, '1×');
    speedInput.addEventListener('input', () => {
      this.viewer.speed = Number(speedInput.value);
      speedValue.textContent = `${num(this.viewer.speed)}×`;
    });
    speed.append(h('span', undefined, t('Velocidad')), speedInput, speedValue);
    controls.append(loop, rotate, speed);

    const raw = h('details', 'gallery-raw');
    raw.append(this.rawClipsSummary, this.rawClipsEl);

    left.append(
      stage,
      h('div', 'gallery-label', t('ANIMACIONES')),
      this.clipsEl,
      this.clipInfoEl,
      controls,
      raw,
      h('div', 'gallery-label', t('EFECTOS VISUALES')),
      this.fxEl,
    );

    // columna derecha: estadísticas, kit y extras
    const right = h('div', 'gallery-right');
    const levelRow = h('label', 'gallery-level');
    this.levelInput.type = 'range';
    this.levelInput.min = '1';
    this.levelInput.max = String(MAX_LEVEL);
    this.levelInput.step = '1';
    this.levelInput.value = '1';
    this.levelInput.addEventListener('input', () => {
      this.level = Number(this.levelInput.value);
      this.renderData();
    });
    levelRow.append(h('span', undefined, t('Nivel')), this.levelInput, this.levelValue);
    const build = this.toggle(t('Con objetos recomendados'), false, (on) => {
      this.withBuild = on;
      this.renderData();
    });
    const head = h('div', 'gallery-stat-head');
    head.append(levelRow, build);

    right.append(
      h('div', 'gallery-label', t('ESTADÍSTICAS')),
      head,
      this.statsEl,
      h('div', 'gallery-label', t('HABILIDADES')),
      this.kitEl,
      h('div', 'gallery-label', t('EN PARTIDA')),
      this.extraEl,
    );

    const body = h('div', 'gallery-body');
    body.append(left, right);
    this.root.append(tabs, body);
  }

  private toggle(label: string, initial: boolean, onChange: (on: boolean) => void): HTMLLabelElement {
    const wrap = h('label', 'gallery-toggle');
    const input = h('input');
    input.type = 'checkbox';
    input.checked = initial;
    input.addEventListener('change', () => onChange(input.checked));
    wrap.append(input, h('span', undefined, label));
    return wrap;
  }

  private select(heroId: HeroId): void {
    this.heroId = heroId;
    this.ranks = { q: 1, w: 1, e: 1, r: 1 };
    for (const [id, tab] of this.tabs) tab.classList.toggle('selected', id === heroId);
    const asset = this.assets.get(heroId);
    const def = getHeroDef(heroId);
    this.nameEl.textContent = def.displayName;
    this.tagsEl.textContent = `${HERO_ROLES[heroId]} · ${def.ranged ? t('A distancia') : t('Cuerpo a cuerpo')}`;

    this.clipsEl.replaceChildren();
    this.rawClipsEl.replaceChildren();
    this.clipButtons.clear();
    this.fxEl.replaceChildren();
    if (asset) {
      for (const [key, clipName] of Object.entries(asset.manifest.clips)) {
        const button = h('button', 'gallery-clip', t(CLIP_LABELS[key] ?? key));
        button.type = 'button';
        button.title = clipName;
        button.dataset.clip = clipName;
        button.addEventListener('click', () => this.viewer.play(clipName));
        this.clipsEl.appendChild(button);
        this.clipButtons.set(`k:${key}`, button);
      }
      const names = [...new Set(asset.clips.map((clip) => clip.name))].sort();
      this.rawClipsSummary.textContent = t('Todos los clips del modelo ({count})', { count: names.length });
      for (const name of names) {
        const button = h('button', 'gallery-clip', name);
        button.type = 'button';
        button.dataset.clip = name;
        button.addEventListener('click', () => this.viewer.play(name));
        this.rawClipsEl.appendChild(button);
      }
      const fx = visualEffects(asset.manifest);
      if (fx.length === 0) fx.push(t('Sin efectos especiales en el modelo; las habilidades usan los efectos genéricos del juego.'));
      for (const line of fx) this.fxEl.appendChild(h('li', undefined, line));
      this.viewer.setHero(asset);
    } else {
      this.clipInfoEl.textContent = t('Modelo no disponible.');
    }
    this.renderData();
  }

  /** Resalta el clip en curso y muestra su duración y para qué se usa. */
  private markClip(clipName: string): void {
    const asset = this.assets.get(this.heroId);
    for (const button of this.root.querySelectorAll<HTMLButtonElement>('.gallery-clip')) {
      button.classList.toggle('playing', button.dataset.clip === clipName);
    }
    const clip = asset?.clips.find((candidate) => candidate.name === clipName);
    const keys = asset
      ? Object.entries(asset.manifest.clips).filter(([, name]) => name === clipName).map(([key]) => CLIP_LABELS[key] ?? key)
      : [];
    const usage = keys.length > 0 ? t('Se usa en: {keys}', { keys: keys.join(', ') }) : t('Clip del modelo sin uso en el juego');
    this.clipInfoEl.textContent = `${clipName} · ${clip ? `${num(clip.duration, 2)} s` : '—'} · ${usage}`;
  }

  private items(): string[] {
    return this.withBuild ? [...(BOT_BUILDS[BUILD_ROLE[this.heroId]] ?? [])] : [];
  }

  private renderData(): void {
    this.levelValue.textContent = String(this.level);
    this.renderStats();
    this.renderKit();
    this.renderExtra();
  }

  private renderStats(): void {
    const heroId = this.heroId;
    const def = getHeroDef(heroId);
    const items = this.items();
    const stats = heroStats(heroId, this.level, items);
    const base = heroStats(heroId, 1);
    const max = heroStats(heroId, MAX_LEVEL);
    const rows: Array<{ label: string; key: keyof HeroStats; format: (value: number) => string; growth?: string }> = [
      { label: 'Vida', key: 'maxHp', format: (v) => num(v, 0), growth: t('+{value}/nivel', { value: num(def.hpPerLevel) }) },
      { label: 'Regeneración', key: 'hpRegen', format: (v) => `${num(v)}/s`, growth: t('fuera de combate') },
      { label: 'Daño de ataque', key: 'attackDamage', format: (v) => num(v), growth: t('+{value}/nivel', { value: num(def.attackDamagePerLevel) }) },
      { label: 'Armadura', key: 'armor', format: (v) => num(v), growth: t('+{value}/nivel', { value: num(def.armorPerLevel) }) },
      { label: 'Vel. de ataque', key: 'attackSpeed', format: (v) => num(v, 2) },
      { label: 'Alcance de ataque', key: 'attackRange', format: () => `${num(def.attacks[0].range * stats.attackRange)} u` },
      { label: 'Vel. de movimiento', key: 'moveSpeed', format: (v) => num(v) },
      { label: 'Aceleración hab.', key: 'abilityHaste', format: (v) => num(v) },
      { label: 'Omnisucción', key: 'omnivamp', format: pct },
      { label: 'Letalidad', key: 'lethality', format: (v) => num(v) },
      { label: 'Penetración', key: 'armorPen', format: pct },
      { label: 'Tenacidad', key: 'tenacity', format: pct },
    ];
    this.statsEl.replaceChildren();
    for (const row of rows) {
      const cell = h('div', 'gallery-stat');
      const value = stats[row.key];
      const top = h('div', 'gallery-stat-top');
      top.append(h('span', undefined, t(row.label)), h('b', undefined, row.format(value)));
      cell.appendChild(top);
      const range = max[row.key] - base[row.key];
      if (range > 0) {
        const bar = h('div', 'gallery-bar');
        const fill = h('i');
        fill.style.width = `${Math.min(100, ((stats[row.key] - base[row.key]) / range) * 100)}%`;
        bar.appendChild(fill);
        cell.appendChild(bar);
      }
      const bonus = value - heroStats(heroId, this.level)[row.key];
      const detail = [row.growth, bonus > 0 ? t('+{value} objetos', { value: row.format(bonus) }) : undefined].filter(Boolean).join(' · ');
      if (detail) cell.appendChild(h('small', undefined, detail));
      this.statsEl.appendChild(cell);
    }
    const fixed = h('div', 'gallery-fixed');
    fixed.append(
      h('span', undefined, t('Reducción de daño: {value}', { value: pct(1 - 100 / (100 + stats.armor)) })),
      h('span', undefined, t('Resistencia al empuje: {value}', { value: pct(def.knockbackResist) })),
      h('span', undefined, t('Radio: {value} u', { value: num(def.radius, 2) })),
    );
    this.statsEl.appendChild(fixed);
  }

  /** Héroe ficticio para reutilizar los cálculos del tooltip del HUD. */
  private fakeHero(): HeroState {
    return { heroId: this.heroId, level: this.level, items: this.items(), ranks: { ...this.ranks } } as unknown as HeroState;
  }

  private renderKit(): void {
    const heroId = this.heroId;
    const def = getHeroDef(heroId);
    const hero = this.fakeHero();
    this.kitEl.replaceChildren();

    for (const slot of ['attack', ...SLOTS] as KitSlot[]) {
      const card = h('article', 'gallery-ability');
      const head = h('header');
      const icon = h('img');
      icon.src = abilityIcon(heroId, slot);
      icon.alt = '';
      const title = h('div', 'gallery-ability-title');
      title.append(
        h('span', 'gallery-key', slot === 'attack' ? 'LMB' : slot.toUpperCase()),
        h('b', undefined, KIT_NAMES[heroId][slot]),
      );
      const kind = slot === 'attack'
        ? (def.ranged ? t('Ataque básico · proyectil teledirigido') : t('Ataque básico · combo cuerpo a cuerpo'))
        : `${t(ABILITY_KIND[def.abilities[slot].kind])}${slot === 'r' ? ` · ${t('definitiva')}` : ''}`;
      title.appendChild(h('small', undefined, kind));
      head.append(icon, title);

      if (slot !== 'attack') {
        const maxRanks = slot === 'r' ? RANKS.ultimateMax : RANKS.basicMax;
        const stepper = h('div', 'gallery-ranks');
        stepper.title = t('Rango de la habilidad');
        for (let rank = 1; rank <= maxRanks; rank++) {
          const pip = h('button', rank <= this.ranks[slot] ? 'on' : undefined, String(rank));
          pip.type = 'button';
          pip.addEventListener('click', () => {
            this.ranks[slot] = rank;
            this.renderKit();
          });
          stepper.appendChild(pip);
        }
        head.appendChild(stepper);
      }
      card.appendChild(head);

      const data = abilityTooltipData(hero, slot);
      card.appendChild(h('p', undefined, KIT_DESCRIPTIONS[heroId][slot] ?? data.description));
      const grid = h('dl');
      for (const stat of data.stats) {
        // en la galería el rango lo elige el usuario: sin avisos de puntos
        if (stat.label === t('Rango')) continue;
        grid.append(h('dt', undefined, stat.label));
        const dd = h('dd', undefined, stat.value);
        if (stat.detail) dd.appendChild(h('small', undefined, stat.detail));
        grid.append(dd);
      }
      if (slot === 'attack') {
        const ratio = attackDamageRatio(heroId, this.level, hero.items);
        def.attacks.forEach((attack, index) => {
          grid.append(h('dt', undefined, t(def.ranged ? 'Disparo {n}' : 'Golpe {n}', { n: index + 1 })));
          const dd = h('dd', undefined, t('{value} daño', { value: num(attack.damage * ratio) }));
          dd.appendChild(h('small', undefined,
            t('animación {time} · activo {from}–{to} · {range} u · empuje {knockback}', { time: secs(attack.duration), from: secs(attack.activeStart), to: secs(attack.activeEnd), range: num(attack.range), knockback: num(attack.knockback) })));
          grid.append(dd);
        });
      } else {
        const ability = def.abilities[slot];
        grid.append(h('dt', undefined, t('Se desbloquea')));
        grid.append(h('dd', undefined, slot === 'r' ? t('niveles 6 / 11 / 16') : t('nivel 1 · máx. rango {max}', { max: RANKS.basicMax })));
        grid.append(h('dt', undefined, t('Animación')));
        grid.append(h('dd', undefined, `${t(CLIP_LABELS[`cast_${slot}`])} · ${secs(abilityDuration(ability))}`));
      }
      card.appendChild(grid);
      if (data.note) card.appendChild(h('div', 'gallery-note', data.note));

      const clipKey = slot === 'attack' ? 'attack1' : `cast_${slot}`;
      const clipName = this.assets.get(heroId)?.manifest.clips[clipKey];
      if (clipName) {
        const preview = h('button', 'gallery-preview', t('Ver animación'));
        preview.type = 'button';
        preview.addEventListener('click', () => this.viewer.play(clipName));
        card.appendChild(preview);
      }
      this.kitEl.appendChild(card);
    }
  }

  private renderExtra(): void {
    const heroId = this.heroId;
    const def = getHeroDef(heroId);
    const role = BUILD_ROLE[heroId];
    this.extraEl.replaceChildren();

    const build = h('div', 'gallery-build');
    for (const id of BOT_BUILDS[role] ?? []) {
      const item = getItem(id);
      if (!item) continue;
      const chip = h('span', 'gallery-item');
      chip.style.setProperty('--item', item.color);
      chip.append(h('i', undefined, item.short), h('span', undefined, `${t(item.name)} · ${item.cost}`));
      build.appendChild(chip);
    }
    const total = (BOT_BUILDS[role] ?? []).reduce((sum, id) => sum + (getItem(id)?.cost ?? 0), 0);

    // reparto por defecto de la partida 3v3 (Link de jugador)
    const team = brawlRoster('knight').blue.includes(heroId) ? t('azul') : t('rojo');
    const asset = this.assets.get(heroId);
    const lines: Array<[string, string | HTMLElement]> = [
      [t('Objetos de los bots'), build],
      [t('Coste de la build'), t('{gold} de oro', { gold: total })],
      [t('Orden de rangos (bots)'), BOT_RANK_PRIORITY.map((slot) => slot.toUpperCase()).join(' › ')],
      [t('Equipo por defecto (3v3)'), team],
      [t('Ataque a distancia'), def.ranged ? t('sí · proyectil {kind}, velocidad {speed}', { kind: def.ranged.projectile, speed: num(def.ranged.speed) }) : t('no')],
      [t('Modelo'), asset ? [t('{height} u de alto', { height: num(asset.manifest.targetHeight, 2) }), `${asset.clips.length} clips`, asset.manifest.equipment ? t('arma {name}', { name: asset.manifest.equipment }) : ''].filter(Boolean).join(' · ') : '—'],
    ];
    const dl = h('dl');
    for (const [label, value] of lines) {
      dl.append(h('dt', undefined, label));
      const dd = h('dd');
      if (typeof value === 'string') dd.textContent = value;
      else dd.appendChild(value);
      dl.append(dd);
    }
    this.extraEl.appendChild(dl);
  }
}

const ABILITY_KIND: Record<string, string> = {
  strike: 'Golpe',
  projectile: 'Proyectil',
  blink: 'Teletransporte',
  shield: 'Escudo',
  grab: 'Agarre',
  execute: 'Ejecución con objetivo',
};
